import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Upload, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet, UploadCloud, RefreshCw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fmt, sha256Hex, ApiError, type System, type Batch, type Carrier } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import ImportChecklist from '../components/ImportChecklist';

export default function Imports() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pollingBatchId, setPollingBatchId] = useState<string | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const queryClient = useQueryClient();

  const batchesQuery = useQuery({
    queryKey: ['batches', sys],
    queryFn: () => api.listBatches(sys ?? undefined),
    enabled: !!sys,
    refetchInterval: pollingBatchId ? 2000 : 30000, // poll fast while a batch is processing
  });

  // Carriers list: drives the per-cycle checklist for both systems, and the
  // Empire carrier-enforcement dropdown.
  const carriersQuery = useQuery({
    queryKey: ['carriers', sys],
    queryFn: () => api.listCarriers(sys as System),
    enabled: !!sys,
  });

  // Pay runs: the checklist's cycle window starts at the latest closed run.
  const payRunsQuery = useQuery({
    queryKey: ['pay-runs', sys],
    queryFn: () => api.listPayRuns(sys as System),
    enabled: !!sys,
  });

  // Stop polling once the batch completes.
  useEffect(() => {
    if (!pollingBatchId || !batchesQuery.data) return;
    const b = batchesQuery.data.batches.find((x) => x.batch_id === pollingBatchId);
    if (b && (b.status === 'complete' || b.status === 'failed')) {
      setPollingBatchId(null);
      if (b.status === 'complete') {
        toast.success(`Imported ${b.row_count} rows from ${b.original_filename ?? 'file'}`);
      } else {
        toast.error(`Import failed: ${b.error_message ?? 'unknown error'}`);
      }
    }
  }, [batchesQuery.data, pollingBatchId, toast]);

  if (!sys) {
    return (
      <div className="alert alert-warning">
        <p>Pick a system from the header switcher.</p>
      </div>
    );
  }

  const [dragOver, setDragOver] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{
    file: File;
    fileHash: string;
    existing: {
      batch_id: string;
      original_filename?: string;
      row_count: number;
      created_at: string;
      carrier?: string | null;
    };
  } | null>(null);

  const performUpload = async (file: File, fileHash: string, force: boolean) => {
    if (!sys) return;
    setUploading(true);
    try {
      const { uploadUrl, batchId, contentType } = await api.uploadUrl(
        sys,
        file.name,
        sys === 'Empire-ACA' ? selectedCarrier : undefined,
        fileHash,
        force,
      );
      await api.uploadFile(uploadUrl, file, contentType);
      toast.info(`Uploaded ${file.name}. Importer is processing — refresh in a few seconds.`);
      setPollingBatchId(batchId);
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.payload?.error === 'duplicate_file') {
        setDuplicateInfo({ file, fileHash, existing: err.payload.existing_batch });
        return;
      }
      console.error(err);
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFilePicked = useCallback(
    async (file: File) => {
      if (!sys) return;
      // Empire-ACA: carrier is optional. When omitted, the importer auto-
      // detects from file content via the carrier dispatcher.
      let hash = '';
      try {
        hash = await sha256Hex(file);
      } catch (err) {
        console.warn('hash failed, uploading without dedupe', err);
      }
      await performUpload(file, hash, false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sys, selectedCarrier],
  );

  // Drag-and-drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only unset if leaving the drop zone itself (not a child)
    if (e.currentTarget === e.target) setDragOver(false);
  };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await onFilePicked(file);
  };

  const confirmDuplicateUpload = async () => {
    if (!duplicateInfo) return;
    const { file, fileHash } = duplicateInfo;
    setDuplicateInfo(null);
    await performUpload(file, fileHash, true);
  };

  const seedEmpireMutation = async () => {
    try {
      const r = await api.seedEmpire();
      toast.success(`Seeded ${r.seeded} Empire records (system + 9 carriers + 14 agents + house).`);
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    } catch (err) {
      toast.error(`Seed failed: ${(err as Error).message}`);
    }
  };

  const carriers = carriersQuery.data?.carriers ?? [];
  const needsSeed = sys === 'Empire-ACA' && !carriersQuery.isLoading && carriers.length === 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          Imports — {sys}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          {sys === 'Messer'
            ? 'Upload one Messer commission file (multiple carriers per file). The importer auto-seeds carrier configs and writing-agent → agent mappings from the lookups sheet.'
            : 'Drop a per-carrier statement (Excel, PDF, or .xls). The carrier is auto-detected from file content. Pick a carrier from the dropdown only if you want to enforce a specific match (e.g. ambiguous file).'}
        </p>
      </div>

      <ImportChecklist
        sys={sys}
        carriers={carriersQuery.data?.carriers ?? []}
        batches={batchesQuery.data?.batches ?? []}
        payRuns={payRunsQuery.data?.pay_runs ?? []}
      />

      {/* Carrier selector for Empire (optional — leave blank for auto-detect) */}
      {sys === 'Empire-ACA' && carriers.length > 0 && (
        <div className="card flex items-end gap-3 flex-wrap">
          <div>
            <label className="label" style={{ marginBottom: '4px' }}>
              Carrier (optional)
            </label>
            <select
              className="select text-sm"
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
              style={{ minWidth: '220px' }}
            >
              <option value="">Auto-detect from file</option>
              {carriers.map((c) => (
                <option key={c.SK} value={c.carrier}>
                  {c.display_name} ({(c as Carrier).format ?? 'excel'})
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs self-center" style={{ color: 'var(--color-text-muted)' }}>
            {selectedCarrier
              ? `Files will be checked against ${selectedCarrier}; mismatches fail.`
              : 'Files are routed by content (Carrier column / State field / sheet name).'}
          </p>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (uploading) return;
          fileInputRef.current?.click();
        }}
        className="card transition-all"
        style={{
          padding: '40px 24px',
          textAlign: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderColor: dragOver ? 'var(--color-primary)' : 'var(--color-border)',
          backgroundColor: dragOver ? 'rgba(34, 211, 238, 0.06)' : undefined,
          opacity: uploading ? 0.5 : 1,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.pdf,.csv,.txt"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFilePicked(f);
          }}
        />
        {uploading ? (
          <>
            <Loader2
              size={36}
              className="mx-auto animate-spin"
              style={{ color: 'var(--color-primary)' }}
            />
            <p className="mt-3 text-base font-medium" style={{ color: 'var(--color-text)' }}>
              Uploading…
            </p>
          </>
        ) : (
          <>
            <UploadCloud
              size={36}
              className="mx-auto"
              style={{ color: dragOver ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
            />
            <p
              className="mt-3 text-base font-medium"
              style={{ color: 'var(--color-text)' }}
            >
              {dragOver ? 'Drop file to upload' : 'Drop a file here, or click to browse'}
            </p>
            <p
              className="mt-1 text-xs"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Accepts .xlsx, .xls, .pdf, .csv, .txt ·{' '}
              {sys === 'Empire-ACA'
                ? selectedCarrier
                  ? `Will enforce carrier: ${selectedCarrier}`
                  : 'Carrier auto-detected from file content'
                : 'One file with all 6 carriers'}
            </p>
          </>
        )}
      </div>

      {needsSeed && (
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-500 shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>
                Empire-ACA needs first-time seeding
              </h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Click below to pre-seed the 9 Empire carrier configs (Ambetter, BCBS variants,
                Molina, Oscar, Caresource) and the 15 paid agents. One-time, idempotent.
              </p>
              <button
                onClick={seedEmpireMutation}
                className="btn btn-primary text-sm mt-3 flex items-center gap-2"
              >
                <Upload size={14} /> Seed Empire-ACA
              </button>
            </div>
          </div>
        </div>
      )}

      <BatchList batches={batchesQuery.data?.batches ?? []} loading={batchesQuery.isLoading} sys={sys} />

      <ConfirmModal
        open={!!duplicateInfo}
        title="You've already imported this exact file"
        message={
          duplicateInfo
            ? `Identical content (same SHA-256) was imported as batch ${duplicateInfo.existing.batch_id} on ${duplicateInfo.existing.created_at} (${duplicateInfo.existing.row_count} rows${duplicateInfo.existing.carrier ? `, ${duplicateInfo.existing.carrier}` : ''}). Importing again will create a duplicate batch and double-count the rows in totals. Continue anyway?`
            : ''
        }
        confirmLabel="Import anyway"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={confirmDuplicateUpload}
        onCancel={() => setDuplicateInfo(null)}
      />
    </div>
  );
}

function BatchList({ batches, loading, sys }: { batches: Batch[]; loading: boolean; sys: System }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reprocessing, setReprocessing] = useState<string | null>(null);

  const handleReprocess = async (batchId: string) => {
    if (!confirm('Re-run the importer on this batch? Existing rows will be wiped and recreated from the original file.')) return;
    setReprocessing(batchId);
    try {
      const r = await api.reprocessBatch(batchId);
      toast.success(`Reprocessed — ${r.deleted_rows} old rows deleted, ${r.reprocessed_batch.row_count} new rows imported`);
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      queryClient.invalidateQueries({ queryKey: ['pay-summary'] });
    } catch (err) {
      toast.error(`Reprocess failed: ${(err as Error).message}`);
    } finally {
      setReprocessing(null);
    }
  };

  if (loading) {
    return (
      <div className="card text-center py-12">
        <Loader2 size={28} className="animate-spin mx-auto" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Loading batches…
        </p>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="card text-center py-12">
        <FileSpreadsheet size={32} className="mx-auto" style={{ color: 'var(--color-text-muted)' }} />
        <h3 className="mt-3 text-base font-medium" style={{ color: 'var(--color-text)' }}>
          No imports yet
        </h3>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Upload your first {sys} commission file above.
        </p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden p-0">
      <table className="table">
        <thead>
          <tr>
            <th>File</th>
            <th>Carrier</th>
            <th>Status</th>
            <th className="text-right">Rows</th>
            <th>Uploaded</th>
            <th>Per-carrier</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => {
            // Single-carrier (Empire) → prominent badge.
            // Multi-carrier (Messer) → "all" placeholder; per-carrier breakdown is already in the next column.
            const carrierLabel = b.carrier ?? (b.extra_meta?.carrier_summary?.length ? 'multi' : null);
            return (
              <tr key={b.batch_id}>
                <td>
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet size={14} style={{ color: 'var(--color-text-muted)' }} />
                    <span className="text-sm">{b.original_filename ?? b.s3_key ?? b.batch_id}</span>
                  </div>
                </td>
                <td>
                  {carrierLabel ? (
                    <span className="badge badge-primary text-xs">{carrierLabel}</span>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      —
                    </span>
                  )}
                </td>
                <td>
                  <StatusBadge status={b.status} />
                  {b.error_message && (
                    <p className="text-xs mt-1 text-red-500">{b.error_message}</p>
                  )}
                </td>
                <td className="text-right text-sm font-mono">{b.row_count.toLocaleString()}</td>
                <td className="text-sm">{fmt.date(b.created_at)}</td>
                <td className="text-xs">
                  {b.extra_meta?.carrier_summary
                    ?.map((c) => `${c.carrier} (${c.rows})`)
                    .join(', ') ?? (b.status === 'complete' ? '—' : '')}
                  {b.extra_meta?.unknown_agents != null && b.extra_meta.unknown_agents > 0 && (
                    <p className="text-amber-500 mt-1">
                      ⚠ {b.extra_meta.unknown_agents} rows had unknown agents
                    </p>
                  )}
                  {b.extra_meta?.duplicate_warning && (
                    <p className="mt-1">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'rgb(245,158,11)' }}>
                        Possible re-upload — {b.extra_meta.duplicate_warning.overlap_pct}% of rows already imported
                      </span>
                    </p>
                  )}
                  {b.extra_meta?.zero_rows_warning && (
                    <p className="mt-1">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: 'rgb(245,158,11)' }}>
                        0 rows imported but statement total ${b.extra_meta.zero_rows_warning.printed_total} — check the file
                      </span>
                    </p>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleReprocess(b.batch_id)}
                      disabled={reprocessing === b.batch_id || b.status === 'pending' || b.status === 'processing'}
                      className="btn-icon"
                      style={{ color: 'var(--color-text-muted)' }}
                      title="Re-run the importer on this file (no need to re-upload)"
                    >
                      {reprocessing === b.batch_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                    </button>
                    <Link
                      to={`/${sys === 'Messer' ? 'messer' : 'empire-aca'}/batches/${b.batch_id}`}
                      className="text-sm"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      View →
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: Batch['status'] }) {
  const cfg = {
    pending: { label: 'Pending', className: 'badge badge-gray', icon: <Loader2 size={12} className="animate-spin" /> },
    processing: { label: 'Processing', className: 'badge badge-warning', icon: <Loader2 size={12} className="animate-spin" /> },
    complete: { label: 'Complete', className: 'badge badge-success', icon: <CheckCircle2 size={12} /> },
    failed: { label: 'Failed', className: 'badge badge-danger', icon: <AlertCircle size={12} /> },
  }[status];
  return (
    <span className={cfg.className} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}
